import {AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {filter, map}                                                   from 'rxjs/operators';
import {BehaviorSubject, combineLatest, Observable, of, Subscription} from 'rxjs';
import {
  ApDynComponentComponent
}                                                                     from '../../ap-dyncomponent/ap-dyncomponent.component';
import {
  ApDynGridColumnConfigBase,
}                                                                     from '../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridPagerConfigBase
}                                                                     from '../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {
  FormStore, IEntryComponent
} from '../../stores/layout/form.store';
import {
  DriverStore
}                                                                     from '../../stores/docu/driver.store';
import {
  MachineStore
}                                                                     from '../../stores/docu/machine.store';
import {
  SettingsStore
}                                                                     from '../../stores/base-data/settings.store';
import {
  LanguageStore
}                                                                     from '../../stores/translation/language.store';
import {
  InstrumentStore
}                                                                     from '../../stores/docu/instrument.store';
import {
  FieldWorkPlanBooksStore
}                                                                     from '../../stores/field-work/field-work-plan-books.store';
import {
  FieldStore
}                                                                     from '../../stores/farm/field.store';
import {
  ApDynGridPagerWizardButton,
  ApDynGridPagerWizardConfig,
  ApDynGridPagerWizardSection
}                                                                     from '../../ap-dyngrids/config/ap-dyn-grid-pager-wizard-config';
import {
  ApUtilService
}                                                                     from '../../ap-utils/service/ap-util.service';
import {
  FwPlanShowGridConfig
}                                                                     from './plan-show/fw-plan-show-grid.config';
import {
  FwPlanShowComponentData,
  IFwPlanShowGridItem
}                                                                     from './plan-show/fw-plan-show.types';

@Component({
  selector: 'fw-plan-show',
  template: `
    <ap-dyncomponent [headerIcon]="'ap-fungidetect-icon'"
                     [loading$]="loading$"
                     [columns]="columns$ | async"
                     [items]="items$ | async"
                     [pager]="pager">
    </ap-dyncomponent>
  `,
  styles: [],
  standalone: false,
  providers: [
    FwPlanShowGridConfig
  ]
})
export class FwPlanShowComponent implements OnInit, AfterViewInit, OnDestroy, IEntryComponent {
  /**
   * Implements IEntryComponent
   */
  public Caption$: BehaviorSubject<string> = new BehaviorSubject('Global__Show');
  @ViewChild(ApDynComponentComponent, {static: true})
  public dynComponent: ApDynComponentComponent;
  @Input() public data: FwPlanShowComponentData;

  public columns$: Observable<ApDynGridColumnConfigBase[]> = of([]);
  public items$: Observable<IFwPlanShowGridItem[]> = of([]);
  public loading$: Observable<boolean> = of(true);
  public pager: ApDynGridPagerConfigBase;

  private _subscriptions: Subscription[] = [];

  constructor(private formStore: FormStore,
              private fieldStore: FieldStore,
              private driverStore: DriverStore,
              private machineStore: MachineStore,
              private settingsStore: SettingsStore,
              private languageStore: LanguageStore,
              private instrumentStore: InstrumentStore,
              private fieldWorkPlanBooksStore: FieldWorkPlanBooksStore,
              private fwPlanShowGridConfig: FwPlanShowGridConfig) {
  }

  /**
   * Initializes the component by setting up loading data, grid columns and pager
   */
  public ngOnInit(): void {
    this.setLoadingData();
    this.setGridColumns();
    this.setPager();
    this.setGridItems();
  }

  /**
   * Performs initialization after the view is ready by setting up grid items and subscribing to selection changes
   */
  public ngAfterViewInit(): void {
    this._subscriptions.push(
      this.dynComponent.SelectedItems$.subscribe((items: IFwPlanShowGridItem[]) => {
        const fieldsToSelect = items.filter(x => x.FieldId)
          .map(i => i.FieldId.toString());
        this.fieldStore.changeSelectedField(fieldsToSelect);
      })
    );
    this._subscriptions.push(
      this.items$.subscribe((items: IFwPlanShowGridItem[]) => {
        const itemsToSelect = items.map(i => i.Id.toString());
        this.dynComponent.dynGrid.gridPaging.setSelectedKeys(itemsToSelect);
      })
    );
  }

  /**
   * Cleans up component by unsubscribing from all subscriptions
   */
  public ngOnDestroy(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
  }

  /**
   * Sets up loading observable by combining loading states of various stores
   */
  private setLoadingData(): void {
    this.loading$ = combineLatest([
      this.fieldStore.Loading$,
      this.driverStore.Loading$,
      this.machineStore.Loading$,
      this.settingsStore.Loading$,
      this.languageStore.Loading$,
      this.instrumentStore.Loading$
    ]).pipe(
      map((loadings) => loadings.some((x) => x))
    );
  }

  /**
   * Configures grid columns based on selected language and settings
   */
  private setGridColumns(): void {
    this.columns$ = combineLatest([
      this.languageStore.SelectedLanguage$,
      this.settingsStore.FirstSetting$
    ]).pipe(
      filter(([language, settings]) => !!language && !!settings),
      map(([language, settings]) => this.fwPlanShowGridConfig.getGridColumns(language, settings))
    );
  }

  /**
   * Sets up pager configuration with back button and wizard sections
   */
  private setPager(): void {
    this.pager = new ApDynGridPagerWizardConfig(
      new ApDynGridPagerWizardButton(
        'Global_Back_With_Arrow',
        ApUtilService.createEventEmitter(this.cancel.bind(this), this._subscriptions)
      ),
      new ApDynGridPagerWizardSection(),
      new ApDynGridPagerWizardSection(),
      undefined
    );
  }

  /**
   * Configures grid items by combining data from multiple stores
   */
  private setGridItems(): void {
    this.items$ = combineLatest([
      this.fieldStore.Fields$,
      this.driverStore.Drivers$,
      this.machineStore.Machines$,
      this.instrumentStore.Instruments$,
      this.fieldWorkPlanBooksStore.PlanBooks$
    ]).pipe(
      filter(([fields, _, _1, _2, planBooks]) => !!fields && !!planBooks),
      map(([_, _1, _2, _3, planBooks]) => {
        return this.fwPlanShowGridConfig.getGridItems(planBooks, this.data.PlanBooksId, this.data.IsBookedItems);
      })
    );
  }

  /**
   * Handles closing the form
   */
  private onClose(): void {
    this.formStore.closeForm();
  }

  private cancel() {
    this.onClose();
    this.formStore.navigateToParamUrl();
  }
}
